// Security review: restrict the client to same-origin API paths and reject responses from old sessions.
import { sessionBoundary } from './session-boundary.js';

/**
 * The single API client.
 *
 * Every screen goes through this. Nothing in the application constructs a fetch
 * of its own, so cookie handling, CSRF, error parsing and abort behaviour exist
 * once rather than being reimplemented slightly differently per page.
 *
 * There is no mock mode and no fixture fallback. If the API is unreachable the
 * screens show an error state, because a screen that renders successfully
 * against fabricated data tells you nothing about whether the product works.
 */

export class ApiError extends Error {
  /** @param {{title: string, code: string, status: number, requestId?: string}} problem */
  constructor(problem) {
    super(problem.title ?? 'Request failed');
    this.name = 'ApiError';
    this.code = problem.code ?? 'UNKNOWN';
    this.status = problem.status ?? 0;
    // Surfaced in the UI so a user reporting a problem can quote something the
    // logs can actually be searched for.
    this.requestId = problem.requestId ?? null;
  }
}

const CSRF_COOKIE = 'cortis_csrf';

function readCsrf() {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CSRF_COOKIE}=([^;]*)`));
  try { return match ? decodeURIComponent(match[1]) : null; } catch { return null; }
}

const expiredListeners = new Set();
export function onSessionExpired(listener) {
  expiredListeners.add(listener);
  return () => expiredListeners.delete(listener);
}

/**
 * @param {string} path
 * @param {RequestInit & {json?: unknown, idempotencyKey?: string}} [options]
 */
export async function api(path, options = {}) {
  if (typeof path !== 'string' || !path.startsWith('/v1/') || path.includes('\\')) {
    throw new Error('API requests must use a same-origin /v1/ path');
  }
  const url = new URL(path, 'https://cortis.invalid');
  if (!url.pathname.startsWith('/v1/')) throw new Error('API requests must use a same-origin /v1/ path');
  const authRequest = url.pathname.startsWith('/v1/auth/');
  const epoch = sessionBoundary.epoch;
  if (!authRequest && !sessionBoundary.ready) throw new ApiError({ title: 'Session is changing. Please retry.', code: 'SESSION_NOT_READY', status: 409 });
  const { json, idempotencyKey, headers = {}, ...rest } = options;
  const method = (rest.method ?? (json !== undefined ? 'POST' : 'GET')).toUpperCase();

  const requestHeaders = new Headers(headers);
  if (!authRequest && sessionBoundary.ownerId) requestHeaders.set('x-cortis-owner', sessionBoundary.ownerId);
  if (json !== undefined) requestHeaders.set('content-type', 'application/json');
  if (idempotencyKey) requestHeaders.set('idempotency-key', idempotencyKey);

  // The CSRF cookie is readable by script on purpose; echoing it in a header is
  // what a cross-site request cannot do.
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = readCsrf();
    if (csrf) requestHeaders.set('x-cortis-csrf', csrf);
  }

  const response = await fetch(url.pathname + url.search, {
    ...rest,
    method,
    headers: requestHeaders,
    credentials: 'same-origin',
    redirect: 'error',
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });

  if (!authRequest) {
    sessionBoundary.assertCurrent(epoch);
  }
  if (response.status === 204) return null;

  const text = await response.text();
  let payload = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!authRequest) {
    sessionBoundary.assertCurrent(epoch);
    if (response.status === 401) for (const listener of expiredListeners) listener(payload?.code);
  }
  if (!response.ok) {
    throw new ApiError({ ...(payload ?? { title: `Request failed (${response.status})` }), status: response.status });
  }
  return payload;
}

/**
 * Subscribe to a run's progress.
 *
 * Reconnects on transport failure, because a dropped stream must not look like
 * a stalled run. It carries no authority of its own: the proof state it reports
 * is whatever the server persisted, and the UI never advances a proof locally.
 *
 * @param {string} runId
 * @param {(event: string, data: any) => void} onEvent
 * @returns {() => void} unsubscribe
 */
export function subscribeToRun(runId, onEvent) {
  let source = null;
  let closed = false;
  let retry = 0;
  let retryTimer = null;
  const epoch = sessionBoundary.epoch;

  const connect = () => {
    if (closed || !sessionBoundary.current(epoch) || !sessionBoundary.ready) return;
    source = new EventSource(`/v1/runs/${encodeURIComponent(runId)}/events${sessionBoundary.ownerId ? `?expectedOwnerId=${encodeURIComponent(sessionBoundary.ownerId)}` : ''}`, { withCredentials: true });

    for (const type of ['proof.state', 'run.terminal', 'error', 'heartbeat']) {
      source.addEventListener(type, (e) => {
        if (closed || !sessionBoundary.current(epoch)) { source?.close(); return; }
        retry = 0;
        try {
          onEvent(type, JSON.parse(e.data));
        } catch {
          /* a malformed frame is not worth tearing the stream down for */
        }
      });
    }

    source.onerror = () => {
      source?.close();
      if (closed || !sessionBoundary.current(epoch)) return;
      retry += 1;
      if (retry > 6) {
        onEvent('error', { code: 'STREAM_LOST' });
        return;
      }
      retryTimer = setTimeout(connect, Math.min(1000 * 2 ** retry, 15_000));
    };
  };

  connect();
  return () => {
    closed = true;
    clearTimeout(retryTimer);
    source?.close();
  };
}

// ---------------------------------------------------------------- endpoints

export const Api = {
  // auth
  guest: () => api('/v1/auth/guest', { json: {} }),
  challenge: (walletAddress) => api('/v1/auth/challenge', { json: { walletAddress } }),
  verify: (body) => api('/v1/auth/verify', { json: body }),
  session: () => api('/v1/auth/session?optional=1'),
  logout: () => api('/v1/auth/logout', { json: {} }),
  logoutAll: () => api('/v1/auth/logout-all', { json: {} }),

  // dashboard
  dashboard: () => api('/v1/dashboard'),
  leaderboard: () => api('/v1/leaderboard'),

  // agents
  agents: () => api('/v1/agents'),
  agent: (id) => api(`/v1/agents/${id}`),
  createAgent: (body) => api('/v1/agents', { json: body }),
  updateAgent: (id, body) => api(`/v1/agents/${id}`, { method: 'PATCH', json: body }),
  deleteAgent: (id) => api(`/v1/agents/${id}`, { method: 'DELETE' }),
  mintIntent: (id) => api(`/v1/agents/${id}/mint-intent`, { json: {} }),
  checkInIntent: (id) => api(`/v1/agents/${id}/check-in-intent`, { json: {} }),
  walletCheckInIntent: () => api('/v1/checkin-intent', { json: {} }),
  walletMapIntent: () => api('/v1/map-intent', { json: {} }),
  walletWorkflowIntent: () => api('/v1/workflow-intent', { json: {} }),
  walletDeployIntent: () => api('/v1/deploy-intent', { json: {} }),
  mapIntent: (id) => api(`/v1/agents/${id}/map-intent`, { json: {} }),
  workflowIntent: (id, workflowId = null) => api(`/v1/agents/${id}/workflow-intent`, { json: workflowId ? { workflowId } : {} }),

  // runs
  createRun: (agentId, message, idempotencyKey) =>
    api(`/v1/agents/${agentId}/runs`, { json: { message }, idempotencyKey }),
  runs: (cursor) => api(`/v1/runs${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
  run: (id) => api(`/v1/runs/${id}`),
  proof: (id) => api(`/v1/runs/${id}/proof`),
  cancelRun: (id) => api(`/v1/runs/${id}/cancel`, { json: {} }),

  // workflows
  workflows: () => api('/v1/workflows'),
  workflow: (id) => api(`/v1/workflows/${id}`),
  createWorkflow: (body) => api('/v1/workflows', { json: body }),
  updateWorkflow: (id, body) => api(`/v1/workflows/${id}`, { method: 'PATCH', json: body }),
  deleteWorkflow: (id) => api(`/v1/workflows/${id}`, { method: 'DELETE' }),
  authorizationPayload: (id) => api(`/v1/workflows/${id}/authorization-payload`, { json: {} }),
  authorizeWorkflow: (id, body) => api(`/v1/workflows/${id}/authorize`, { json: body }),
  pauseWorkflow: (id) => api(`/v1/workflows/${id}/pause`, { json: {} }),
  resumeWorkflow: (id) => api(`/v1/workflows/${id}/resume`, { json: {} }),
  revokeWorkflow: (id) => api(`/v1/workflows/${id}/revoke`, { json: {} }),
  workflowRuns: (id) => api(`/v1/workflows/${id}/runs`),
  runWorkflowNow: (id, idempotencyKey = crypto.randomUUID()) => api(`/v1/workflows/${id}/run-now`, { json: {}, idempotencyKey }),

  // chain
  registerTransaction: (body) => api('/v1/transactions', { json: body }),
  transaction: (id) => api(`/v1/transactions/${id}`),
  activity: () => api('/v1/activity'),

  // token
  corStatus: () => api('/v1/cor/status'),
  corBalances: () => api('/v1/cor/balances'),

  // map
  map: (prompt) => api('/v1/map', { json: { prompt } }),

  // privacy
  sessions: () => api('/v1/privacy/sessions'),
  erase: () => api('/v1/privacy/erase', { json: { confirm: 'ERASE' } }),
};
